import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { animate, useReducedMotion, type AnimationPlaybackControls, type Transition } from 'framer-motion'

/**
 * Breathing room left above the anchored message, so it reads as sitting at the
 * top of the conversation rather than jammed against the header. Exported so
 * the carry pin (nested/scroller.tsx) derives the same anchor position.
 */
export const ANCHOR_TOP_GAP_PX = 16

/**
 * Carries the anchored message up to the top edge. An ease-out tween: the whole
 * transcript moves with it, and any overshoot or oscillation of a wall of text
 * reads as a glitch rather than as motion.
 */
const ANCHOR_EASE: Transition = { type: 'tween', duration: 0.35, ease: 'easeOut' }

/**
 * Sizes a filler element below the transcript so the newest turn can scroll up
 * to the top edge.
 *
 * The filler is sized so that the last user message's top lands `ANCHOR_TOP_GAP_PX`
 * below the top of the viewport once the list is scrolled to its bottom — that
 * is, `scrollHeight - clientHeight === offsetTop(lastUserMessage) - gap`. That
 * makes the existing bottom-follow pin do the anchoring for free: no second
 * scroll authority to fight with it mid-stream, and no new behaviour on the
 * paths that open a chat or jump to a message.
 *
 * Because the filler shrinks by exactly what the reply grows, scrollHeight
 * holds still while the assistant streams: the pin writes the same scrollTop
 * every frame, the user message stays put, and the reply fills the space
 * beneath it. Once a turn outgrows the viewport the filler is 0 and the
 * anchor holds still — the pin's target is capped at the anchor, so growth
 * past the viewport never scrolls the view.
 *
 * Growing the filler is what carries the message upward, so easing its
 * height *is* the scroll animation — the pin rides the resize and scrollTop
 * stays exactly at the bottom throughout. Animating the scroll instead would
 * put a second authority on the same axis, which is the race that made the
 * earlier smooth edge-jumps snap back.
 *
 * The height is written straight to the node rather than through state so it
 * lands in the same frame as the resize that prompted it — the pin measures
 * `scrollHeight` from the same ResizeObserver delivery, and a height still
 * waiting on a React commit would leave it pinning to a stale bottom.
 */
export function useLastTurnSpacer(
  scrollElement: HTMLElement | null,
  lastUserMessageId: string | null,
  lastUserMessageIndex: number,
) {
  const spacerRef = useRef<HTMLDivElement | null>(null)
  const prefersReducedMotion = useReducedMotion()

  const targetRef = useRef<number | null>(null)
  const animationRef = useRef<AnimationPlaybackControls | null>(null)
  const anchoredTurnRef = useRef<number | null>(null)

  const applyHeight = useCallback((next: number, animated: boolean) => {
    const spacer = spacerRef.current
    if (!spacer) return
    // Already resting at, or travelling toward, this height. Re-deriving the
    // same target mid-animation must not restart it.
    if (targetRef.current !== null && Math.abs(targetRef.current - next) < 1) return
    targetRef.current = next

    animationRef.current?.stop()
    animationRef.current = null

    const current = parseFloat(spacer.style.height) || 0
    if (!animated || Math.abs(current - next) < 1) {
      spacer.style.height = `${next}px`
      return
    }

    animationRef.current = animate(current, next, {
      ...ANCHOR_EASE,
      onUpdate: (value) => {
        spacer.style.height = `${value}px`
      },
      onComplete: () => {
        animationRef.current = null
      },
    })
  }, [])

  const measure = useCallback(() => {
    const spacer = spacerRef.current
    if (!scrollElement || !spacer) return

    const lastUserMessage = lastUserMessageId ? document.getElementById(`message-${lastUserMessageId}`) : null
    if (!lastUserMessage) {
      applyHeight(0, false)
      return
    }

    // Measured from the spacer's own top rather than the content's bottom so
    // the reading excludes the spacer itself — otherwise every measurement
    // would feed its own result back in and the height would never settle.
    const turnHeight = spacer.getBoundingClientRect().top - lastUserMessage.getBoundingClientRect().top
    // The composer floats over the scroller's bottom padding, so that band is
    // not somewhere a message may come to rest.
    const composerInset = parseFloat(getComputedStyle(scrollElement).paddingBottom) || 0
    const next = Math.max(0, scrollElement.clientHeight - composerInset - ANCHOR_TOP_GAP_PX - turnHeight)

    // Only a *change* of anchor animates. The first anchor of a mount is the
    // chat opening on its history, which should already be settled — and while
    // the animation is still travelling, a re-target keeps animating rather
    // than cutting to the new value, so the reply's first tokens landing
    // mid-flight don't snap it.
    //
    // Keyed on the turn's index, not the message id: `metadata` renames the
    // optimistic user message to its backend id mid-turn, and reading that as a
    // new anchor would animate a height this turn had already settled into.
    const anchorChanged = anchoredTurnRef.current !== null && anchoredTurnRef.current !== lastUserMessageIndex
    anchoredTurnRef.current = lastUserMessageIndex
    const animated = !prefersReducedMotion && (anchorChanged || animationRef.current !== null)

    applyHeight(next, animated)
  }, [scrollElement, lastUserMessageId, lastUserMessageIndex, prefersReducedMotion, applyHeight])

  // Read through a ref so the observer below can depend on `scrollElement`
  // alone. Re-subscribing whenever the measurement changes would churn an
  // observer that has nothing to re-observe.
  const measureRef = useRef(measure)
  measureRef.current = measure

  // Layout effect, not passive: the open path pins the list to its bottom from
  // a layout effect of its own, and a spacer still unsized when that runs pins
  // a short last turn to the bottom edge — then jumps it up to the anchor once
  // a passive pass sizes the filler. Measuring in the commit phase means the
  // open pin already measures the anchored bottom, so the chat paints once.
  useLayoutEffect(() => {
    if (!scrollElement) return

    const run = () => measureRef.current()
    run()

    const observer = new ResizeObserver(run)
    observer.observe(scrollElement)
    // The content wrapper as well as the scroller: the scroller's own box is
    // fixed by the surrounding layout, so only the wrapper reports the turn
    // growing as tokens land. The spacer is deliberately not observed — it is
    // what this writes to.
    const content = scrollElement.firstElementChild
    if (content) observer.observe(content)
    return () => observer.disconnect()
  }, [scrollElement])

  // A newly sent message is a new anchor, and it may be the same height as the
  // one it replaces — so re-measure on identity, not on the resize alone. The
  // id is in here too so the mid-turn rename re-points the DOM lookup; that
  // pass re-derives the same height and, not being an anchor change, is silent.
  useLayoutEffect(() => {
    measureRef.current()
  }, [lastUserMessageId, lastUserMessageIndex])

  useEffect(() => () => animationRef.current?.stop(), [])

  return spacerRef
}
